import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useNotificationsContext } from "@/contexts/NotificationsContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Send, Loader2, MessageCircle, CheckCircle2, Lock, ArrowLeft, Plane, Building2, Car, Utensils, MoreHorizontal, Pin, ArrowRight, ChevronDown, ChevronUp, Archive } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { format, isToday, isYesterday } from "date-fns";
import BottomNav from "@/components/BottomNav";
import { FileUpload } from "@/components/chat/FileUpload";
import { MessageAttachment } from "@/components/chat/MessageAttachment";
import { TruncatedMessage } from "@/components/chat/TruncatedMessage";
import { ScrollToBottomButton } from "@/components/chat/ScrollToBottomButton";
import { MessageStatus } from "@/components/chat/MessageStatus";
import { TypingIndicator } from "@/components/chat/TypingIndicator";
import { MessageAvatar } from "@/components/chat/MessageAvatar";
import { SystemMessage } from "@/components/chat/SystemMessage";
import { useTypingIndicator } from "@/hooks/useTypingIndicator";

interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  is_from_agent: boolean;
  is_read: boolean;
  read_at: string | null;
  delivered_at: string | null;
  attachment_url: string | null;
  attachment_name: string | null;
  attachment_type: string | null;
  created_at: string;
  is_pinned: boolean;
  pinned_at: string | null;
  pinned_by: string | null;
  is_starred: boolean;
  starred_at: string | null;
  starred_by: string | null;
  is_system_message?: boolean;
}

interface Service {
  id: string;
  name: string;
  welcome_message: string | null;
}

interface Conversation {
  id: string;
  user_id: string;
  status: "open" | "pending" | "closed";
  subject: string | null;
  unread_count_user: number;
  service_id: string | null;
  is_archived?: boolean;
  archived_at?: string | null;
  assigned_agent_id?: string | null;
  service?: Service | null;
}

interface AgentProfile {
  avatar_url: string | null;
  first_name: string | null;
  last_name: string | null;
}



export default function ChatPage() {
  const { user, isAgent } = useAuth();
  const { refreshUnreadCount } = useNotificationsContext();
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [agentProfile, setAgentProfile] = useState<AgentProfile | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [showPinnedPanel, setShowPinnedPanel] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const hasMessagesSentRef = useRef(false);

  // Typing indicator - only active when we have a conversation
  const { typingUsers, sendTyping, stopTyping, isTyping } = useTypingIndicator(conversationId || "");

  // Track if user has sent at least one message
  useEffect(() => {
    if (messages.length > 0 && messages.some(m => !m.is_from_agent)) {
      hasMessagesSentRef.current = true;
    }
  }, [messages]);

  // Cleanup empty conversation on unmount with safety delay
  useEffect(() => {
    const convId = conversationId;
    const hasSentMessages = hasMessagesSentRef.current;
    
    return () => {
      // If user leaves without sending any message, delete the conversation
      // Add a delay to prevent race conditions with quick navigation
      if (convId && !hasSentMessages) {
        const timeoutId = setTimeout(async () => {
          // Double-check the conversation still has no messages before deleting
          const { data: msgCheck } = await supabase
            .from("messages")
            .select("id")
            .eq("conversation_id", convId)
            .limit(1);
          
          if (!msgCheck || msgCheck.length === 0) {
            const { error } = await supabase
              .from("conversations")
              .delete()
              .eq("id", convId);
            
            if (error) {
              console.log("[ChatPage] Could not delete empty conversation:", error);
            } else {
              console.log("[ChatPage] Deleted empty conversation:", convId);
            }
          }
        }, 2000); // 2 second delay
        
        // Note: This timeout won't be cleared if component unmounts, which is intentional
        // The cleanup should still happen even after unmount
      }
    };
  }, [conversationId]);

  // Note: Agent redirect is now handled by ProtectedRoute userOnly prop

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  const scrollToMessage = useCallback((messageId: string) => {
    const messageElement = document.getElementById(`message-${messageId}`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: "smooth", block: "center" });
      // Highlight animation
      messageElement.classList.add("ring-2", "ring-primary", "ring-offset-2");
      setTimeout(() => {
        messageElement.classList.remove("ring-2", "ring-primary", "ring-offset-2");
      }, 2000);
    }
  }, []);

  // Handle scroll to show/hide scroll button
  const handleScroll = useCallback(() => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      // Show button if more than 100vh from bottom
      setShowScrollButton(distanceFromBottom > window.innerHeight);
    }
  }, []);

  // Fetch conversation and messages
  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      let convData;
      
      if (conversationId) {
        // Fetch specific conversation
        const { data, error } = await supabase
          .from("conversations")
          .select(`
            *,
            service:services(id, name, welcome_message, bg_image_url)
          `)
          .eq("id", conversationId)
          .eq("user_id", user.id)
          .maybeSingle();
        
        if (error || !data) {
          console.error("Error fetching conversation:", error);
          navigate("/chat", { replace: true });
          return;
        }
        convData = data;
      } else {
        // Redirect to conversations list
        navigate("/chat", { replace: true });
        return;
      }

      if (convData) {
        setConversation(convData as Conversation);
        
        // Fetch agent profile if there's an assigned agent
        if (convData.assigned_agent_id) {
          const { data: agentData, error: agentError } = await supabase
            .from("profiles")
            .select("avatar_url, first_name, last_name")
            .eq("id", convData.assigned_agent_id)
            .maybeSingle();
          
          console.log('[ChatPage] Agent profile fetch:', { agentData, agentError, agentId: convData.assigned_agent_id });
          
          if (agentData) {
            setAgentProfile(agentData);
          }
        } else {
          console.log('[ChatPage] No assigned agent for this conversation');
        }

        // Fetch messages
        const { data: msgData, error: msgError } = await supabase
          .from("messages")
          .select("*")
          .eq("conversation_id", convData.id)
          .order("created_at", { ascending: true });

        if (!msgError && msgData) {
          setMessages(msgData as Message[]);
          
          // Pre-fill welcome message if no messages yet
          if (msgData.length === 0 && convData.service?.welcome_message) {
            setNewMessage(convData.service.welcome_message);
          }
        }

        // Mark messages as read
        await supabase
          .from("messages")
          .update({ is_read: true, read_at: new Date().toISOString() })
          .eq("conversation_id", convData.id)
          .eq("is_from_agent", true)
          .eq("is_read", false);

        // Reset unread count and wait for it to be applied
        const { error: updateError } = await supabase
          .from("conversations")
          .update({ unread_count_user: 0 })
          .eq("id", convData.id);

        if (updateError) {
          console.error('[ChatPage] Error resetting unread count:', updateError);
        } else {
          // Rafraîchir le badge après un petit délai pour s'assurer que la DB a committé
          setTimeout(() => refreshUnreadCount(), 100);
        }
      }

      setLoading(false);
    };

    fetchData();
  }, [user, conversationId, navigate]);

  // Scroll to bottom after initial load
  useEffect(() => {
    if (!loading && messages.length > 0) {
      // Use instant scroll on initial load
      setTimeout(() => scrollToBottom("instant"), 100);
    }
  }, [loading, scrollToBottom]);

  // Real-time subscription for messages and conversation updates
  useEffect(() => {
    if (!conversation) return;

    const messagesChannel = supabase
      .channel(`messages-${conversation.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `conversation_id=eq.${conversation.id}`,
        },
        (payload) => {
          const newMsg = payload.new as Message;
          setMessages((prev) => {
            if (prev.find((m) => m.id === newMsg.id)) return prev;
            return [...prev, newMsg];
          });

          // Mark as read if from agent and reset unread count
          if (newMsg.is_from_agent) {
            supabase
              .from("messages")
              .update({ is_read: true, read_at: new Date().toISOString() })
              .eq("id", newMsg.id);
            
            // Mettre à jour le compteur et rafraîchir le badge
            supabase
              .from("conversations")
              .update({ unread_count_user: 0 })
              .eq("id", conversation.id)
              .then(() => refreshUnreadCount());
          }

          scrollToBottom();
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "messages",
          filter: `conversation_id=eq.${conversation.id}`,
        },
        (payload) => {
          // Update message read status in real-time
          const updatedMsg = payload.new as Message;
          setMessages((prev) =>
            prev.map((m) => (m.id === updatedMsg.id ? updatedMsg : m))
          );
        }
      )
      .subscribe();

    // Subscribe to conversation status changes
    const convChannel = supabase
      .channel(`conv-status-${conversation.id}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "conversations",
          filter: `id=eq.${conversation.id}`,
        },
        (payload) => {
          const updated = payload.new as Conversation;
          setConversation(prev => prev ? { ...prev, ...updated } : null);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(messagesChannel);
      supabase.removeChannel(convChannel);
    };
  }, [conversation, scrollToBottom]);

  const handleSend = async () => {
    if (!newMessage.trim() || !conversation || !user || sending) return;

    // Allow sending in archived conversations (will reactivate them)

    setSending(true);
    const messageContent = newMessage.trim();
    setNewMessage("");

    const { error } = await supabase.from("messages").insert({
      conversation_id: conversation.id,
      sender_id: user.id,
      content: messageContent,
      is_from_agent: false,
      delivered_at: new Date().toISOString(), // Message is delivered immediately
    });

    if (error) {
      toast.error("Échec de l'envoi du message");
      setNewMessage(messageContent);
    } else {
      // Scroll to bottom after sending
      setTimeout(() => scrollToBottom(), 100);
    }

    setSending(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatMessageTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return format(date, "HH:mm");
  };

  const formatDateHeader = (dateStr: string) => {
    const date = new Date(dateStr);
    if (isToday(date)) return "Aujourd'hui";
    if (isYesterday(date)) return "Hier";
    return format(date, "d MMMM yyyy");
  };

  const getDateGroups = () => {
    const groups: { date: string; messages: Message[] }[] = [];
    let currentDate = "";

    messages.forEach((msg) => {
      const msgDate = format(new Date(msg.created_at), "yyyy-MM-dd");
      if (msgDate !== currentDate) {
        currentDate = msgDate;
        groups.push({ date: msg.created_at, messages: [msg] });
      } else {
        groups[groups.length - 1].messages.push(msg);
      }
    });

    return groups;
  };

  const getStatusDisplay = () => {
    if (!conversation) return null;
    
    switch (conversation.status) {
      case "open":
        return {
          label: "En cours",
          icon: <MessageCircle size={14} />,
          className: "bg-success/10 text-success border-success/20"
        };
      case "pending":
        return {
          label: "En attente",
          icon: <MessageCircle size={14} />,
          className: "bg-warning/10 text-warning border-warning/20"
        };
      default:
        return null;
    }
  };

  const getServiceIcon = () => {
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const statusDisplay = getStatusDisplay();
  const isArchived = conversation?.is_archived;

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden">
      {/* Header */}
      <header 
        className="shrink-0 z-10 glass border-b border-border px-3 sm:px-4 py-2.5 sm:py-3"
        style={{ paddingTop: 'max(2.5rem, calc(env(safe-area-inset-top, 0px) + 1rem))' }}
      >
        <div className="flex items-center gap-2 sm:gap-3 w-full">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/chat")}
            className="shrink-0 h-9 w-9"
          >
            <ArrowLeft size={18} />
          </Button>
          <Avatar className="w-9 h-9 sm:w-10 sm:h-10">
            {agentProfile?.avatar_url && (
              <AvatarImage src={agentProfile.avatar_url} alt="Concierge" />
            )}
            <AvatarFallback className="bg-primary/10 text-primary font-serif font-semibold text-sm sm:text-base">
              {agentProfile?.first_name?.charAt(0)?.toUpperCase() || "C"}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
              <h1 className="font-serif font-medium text-foreground text-sm sm:text-base">
                Concierge
              </h1>
              {conversation?.service?.name && (
                <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 text-[9px] sm:text-[10px] hidden xs:inline-flex">
                  {getServiceIcon()}
                  <span className="ml-1">{conversation.service.name}</span>
                </Badge>
              )}
              {statusDisplay && (
                <Badge variant="outline" className={`text-[9px] sm:text-[10px] ${statusDisplay.className}`}>
                  {statusDisplay.icon}
                  <span className="ml-1">{statusDisplay.label}</span>
                </Badge>
              )}
              {isArchived && (
                <Badge variant="outline" className="bg-muted text-muted-foreground border-muted text-[9px] sm:text-[10px]">
                  <Archive size={10} className="mr-1" />
                  Traitée
                </Badge>
              )}
            </div>
            <p className="text-[10px] sm:text-xs text-muted-foreground truncate">
              {isArchived 
                ? "Répondez pour réactiver cette conversation"
                : "Nous répondons généralement en quelques minutes"
              }
            </p>
          </div>
          {!isArchived && (
            <div className="w-2 h-2 rounded-full bg-success animate-pulse shrink-0" />
          )}
        </div>
      </header>

      {/* Pinned Messages Bar */}
      {messages.filter(m => m.is_pinned).length > 0 && (
        <div className="bg-primary/5 border-b border-primary/20">
          <button 
            className="w-full px-4 py-2 flex items-center justify-between hover:bg-primary/10 transition-colors"
            onClick={() => setShowPinnedPanel(!showPinnedPanel)}
          >
            <div className="flex items-center gap-2 text-xs text-primary font-medium">
              <Pin size={12} />
              <span>{messages.filter(m => m.is_pinned).length} message{messages.filter(m => m.is_pinned).length > 1 ? "s" : ""} épinglé{messages.filter(m => m.is_pinned).length > 1 ? "s" : ""}</span>
            </div>
            {showPinnedPanel ? <ChevronUp size={14} className="text-primary" /> : <ChevronDown size={14} className="text-primary" />}
          </button>
          {showPinnedPanel && (
            <div className="px-4 pb-3 space-y-2 max-h-48 overflow-auto">
              {messages.filter(m => m.is_pinned).map((msg) => (
                <div 
                  key={`pinned-${msg.id}`} 
                  className="bg-background rounded-lg px-3 py-2 flex items-start justify-between gap-2 group"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground line-clamp-2">{msg.content}</p>
                    <span className="text-[10px] text-muted-foreground">
                      {msg.is_from_agent ? "Concierge" : "Vous"} • {formatMessageTime(msg.created_at)}
                    </span>
                  </div>
                  <button
                    className="h-7 px-2 text-xs flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 text-primary hover:bg-primary/10 rounded"
                    onClick={() => {
                      scrollToMessage(msg.id);
                      setShowPinnedPanel(false);
                    }}
                  >
                    Aller
                    <ArrowRight size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Messages */}
      <main 
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-auto px-3 sm:px-4 py-3 sm:py-4"
        style={{ paddingBottom: 'calc(8rem + max(20px, env(safe-area-inset-bottom, 0px)))' }}
      >
        <div className="w-full max-w-4xl mx-auto">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-4 sm:px-8">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                {conversation?.service?.name ? (
                  getServiceIcon()
                ) : (
                  <span className="text-primary font-serif text-xl sm:text-2xl font-semibold">
                    C
                  </span>
                )}
              </div>
              <h2 className="font-serif text-lg sm:text-xl font-medium text-foreground mb-2">
                {conversation?.service?.name || "Bienvenue"}
              </h2>
              <p className="text-xs sm:text-sm text-muted-foreground max-w-xs">
              Votre assistant personnel est prêt à vous aider. Envoyez un message pour commencer.
            </p>
          </div>
        ) : (
          <div className="space-y-4">

            {getDateGroups().map((group, groupIndex) => (
              <div key={groupIndex}>
                {/* Date header */}
                <div className="flex justify-center my-4">
                  <span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">
                    {formatDateHeader(group.date)}
                  </span>
                </div>

                {/* Messages */}
                <div className="space-y-2">
                  {group.messages.map((msg) => (
                    msg.is_system_message ? (
                      <SystemMessage 
                        key={msg.id}
                        content={msg.content} 
                        createdAt={msg.created_at} 
                      />
                    ) : (
                    <div
                      key={msg.id}
                      id={`message-${msg.id}`}
                      className={`flex items-end gap-1.5 ${
                        msg.is_from_agent ? "justify-start" : "justify-end"
                      } animate-fade-in transition-all duration-300`}
                    >
                      {/* Avatar for agent messages (left side) */}
                      {msg.is_from_agent && (
                        <MessageAvatar 
                          isFromAgent={true} 
                          avatarUrl={agentProfile?.avatar_url}
                          firstName={agentProfile?.first_name}
                          lastName={agentProfile?.last_name}
                          size="xs" 
                        />
                      )}
                      <div
                        className={`max-w-[75%] px-4 py-2.5 relative ${
                          msg.is_from_agent
                            ? "chat-bubble-agent"
                            : "chat-bubble-user"
                        } ${msg.is_pinned ? "ring-1 ring-primary/30" : ""}`}
                      >
                        {msg.is_pinned && (
                          <Pin size={10} className="absolute top-1 right-1 text-primary" />
                        )}
                        <TruncatedMessage content={msg.content} maxHeight={50} />
                        {msg.attachment_url && msg.attachment_name && msg.attachment_type && (
                          <MessageAttachment
                            url={msg.attachment_url}
                            name={msg.attachment_name}
                            type={msg.attachment_type}
                            isFromAgent={msg.is_from_agent}
                          />
                        )}
                        <div
                          className={`flex items-center gap-1 mt-1 ${
                            msg.is_from_agent ? "justify-start" : "justify-end"
                          }`}
                        >
                          <span
                            className={`text-[10px] ${
                              msg.is_from_agent
                                ? "text-muted-foreground"
                                : "text-primary-foreground/70"
                            }`}
                          >
                            {formatMessageTime(msg.created_at)}
                          </span>
                          {!msg.is_from_agent && (
                            <MessageStatus
                              createdAt={msg.created_at}
                              deliveredAt={msg.delivered_at}
                              readAt={msg.read_at}
                              isRead={msg.is_read}
                              isFromAgent={msg.is_from_agent}
                            />
                          )}
                        </div>
                      </div>
                      {/* Avatar for user messages (right side) */}
                      {!msg.is_from_agent && (
                        <MessageAvatar isFromAgent={false} size="xs" />
                      )}
                    </div>
                    )
                  ))}
                </div>
              </div>
            ))}
            
            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-2xl px-4 py-2">
                  <TypingIndicator typingUsers={typingUsers} />
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        )}
        </div>
      </main>

      {/* Scroll to bottom button */}
      <ScrollToBottomButton 
        visible={showScrollButton} 
        onClick={() => scrollToBottom()} 
      />

      {/* Input - positioned above BottomNav */}
      <div 
        className="fixed left-0 right-0 glass border-t border-border px-3 sm:px-4 py-2 z-20"
        style={{ bottom: 'calc(4rem + max(20px, env(safe-area-inset-bottom, 0px)))' }}
      >
        <div className="w-full max-w-4xl mx-auto">
          <div className="flex items-end gap-2">
            <FileUpload
              disabled={sending}
              onFileUploaded={async (url, name, type) => {
                if (!conversation || !user) return;
                const { error } = await supabase.from("messages").insert({
                  conversation_id: conversation.id,
                  sender_id: user.id,
                  content: `📎 ${name}`,
                  is_from_agent: false,
                  attachment_url: url,
                  attachment_name: name,
                  attachment_type: type,
                  delivered_at: new Date().toISOString(),
                });
                if (error) {
                  toast.error("Échec de l'envoi du fichier");
                  console.error("File upload message error:", error);
                }
              }}
            />
            <div className="flex-1">
              <Textarea
                ref={inputRef}
                value={newMessage}
                onChange={(e) => {
                  setNewMessage(e.target.value);
                  sendTyping();
                  // Auto-resize textarea
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                onBlur={stopTyping}
                placeholder={isArchived ? "Répondre pour réactiver..." : "Votre message..."}
                rows={1}
                className="w-full min-h-[44px] max-h-[120px] py-2.5 px-4 resize-none rounded-2xl bg-muted/50 border-0 focus-visible:ring-1 focus-visible:ring-primary/30 overflow-y-auto"
                style={{ height: '44px' }}
              />
            </div>
            <Button
              variant="gold"
              size="icon"
              onClick={handleSend}
              disabled={!newMessage.trim() || sending}
              className="shrink-0 h-11 w-11 rounded-full"
            >
              {sending ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Send size={18} />
              )}
            </Button>
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
